import React, { useEffect, useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, Lock, Heart, Diamond, Star } from 'lucide-react';
import { MaschaSkinsData } from './MaschaSkinsData';
interface MaschaSkinsCarouselProps {
  onSkinSelect: (skinId: string) => void;
  unlockedSkins: string[];
}
export const MaschaSkinsCarousel: React.FC<MaschaSkinsCarouselProps> = ({
  onSkinSelect,
  unlockedSkins
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Auto-scroll carousel
  useEffect(() => {
    if (!isAutoScrolling) return;
    const scrollNext = () => {
      setCurrentIndex(prevIndex => (prevIndex + 1) % MaschaSkinsData.length);
    };
    timeoutRef.current = setTimeout(scrollNext, 3000);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [currentIndex, isAutoScrolling]);
  // Pause auto-scroll on user interaction
  const pauseAutoScroll = () => {
    setIsAutoScrolling(false);
    // Resume after 10 seconds of inactivity
    setTimeout(() => setIsAutoScrolling(true), 10000);
  };
  const handlePrev = () => {
    pauseAutoScroll();
    setCurrentIndex(prevIndex => prevIndex === 0 ? MaschaSkinsData.length - 1 : prevIndex - 1);
  };
  const handleNext = () => {
    pauseAutoScroll();
    setCurrentIndex(prevIndex => (prevIndex + 1) % MaschaSkinsData.length);
  };
  const handleDotClick = (index: number) => {
    pauseAutoScroll();
    setCurrentIndex(index);
  };
  // Determine visible cards (current, previous, next)
  const visibleSkins = [MaschaSkinsData[(currentIndex - 1 + MaschaSkinsData.length) % MaschaSkinsData.length], MaschaSkinsData[currentIndex], MaschaSkinsData[(currentIndex + 1) % MaschaSkinsData.length]];
  return <div className="relative w-full py-6 mb-8" data-id="element-9455">
      <h2 className="text-lg font-bold mb-4 text-center" data-id="element-9456">
        Mascha's Exclusive Skins Collection
      </h2>
      {/* Main carousel */}
      <div className="relative overflow-hidden mx-auto max-w-4xl px-12" data-id="element-9457">
        <div className="flex items-center justify-center gap-4" data-id="element-9458">
          {visibleSkins.map((skin, index) => {
          const isCenter = index === 1;
          const isUnlocked = unlockedSkins.includes(skin.id);
          return <div key={skin.id} className={`relative group transition-all duration-500 ${isCenter ? 'w-64 h-80 z-20 scale-110' : 'w-48 h-64 opacity-60 z-10'}`} onClick={() => {
            if (isCenter) {
              onSkinSelect(skin.id);
            } else {
              pauseAutoScroll();
              setCurrentIndex(MaschaSkinsData.findIndex(s => s.id === skin.id));
            }
          }} data-id="element-9459">
                <div className={`w-full h-full rounded-lg overflow-hidden shadow-lg ${isCenter ? 'border-2 border-amber-500' : 'border border-gray-700'}`} data-id="element-9460">
                  <img src={skin.preview_image_url} alt={skin.skin_name} className={`w-full h-full object-cover object-top transition-transform duration-700 ${isCenter ? 'group-hover:scale-105' : ''} ${!isUnlocked ? 'grayscale opacity-70' : ''}`} data-id="element-9461" />
                  {/* Locked overlay */}
                  {!isUnlocked && <div className="absolute inset-0 bg-black/50 flex items-center justify-center" data-id="element-9462">
                      <Lock size={isCenter ? 36 : 24} className="text-white opacity-80" data-id="element-9463" />
                    </div>}
                  {/* Skin info overlay */}
                  <div className={`absolute bottom-0 inset-x-0 bg-black/70 backdrop-blur-sm p-3 transition-all ${isCenter ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} data-id="element-9464">
                    <div className="flex items-center justify-between mb-1" data-id="element-9465">
                      <h3 className="font-medium text-white text-sm" data-id="element-9466">
                        {skin.skin_name}
                      </h3>
                      {skin.rarity === 'Legendary' && <div className="flex items-center" data-id="element-9467">
                          <Star size={12} className="text-amber-400" fill="currentColor" data-id="element-9468" />
                          <Star size={12} className="text-amber-400" fill="currentColor" data-id="element-9469" />
                          <Star size={12} className="text-amber-400" fill="currentColor" data-id="element-9470" />
                        </div>}
                      {skin.rarity === 'Epic' && <div className="flex items-center" data-id="element-9471">
                          <Star size={12} className="text-purple-400" fill="currentColor" data-id="element-9472" />
                          <Star size={12} className="text-purple-400" fill="currentColor" data-id="element-9473" />
                        </div>}
                      {skin.rarity === 'Rare' && <div className="flex items-center" data-id="element-9474">
                          <Star size={12} className="text-blue-400" fill="currentColor" data-id="element-9475" />
                        </div>}
                    </div>
                    {!isUnlocked && <div className="flex items-center text-xs" data-id="element-9476">
                        <span className="text-gray-300 mr-1" data-id="element-9477">Price:</span>
                        <span className="font-bold text-white" data-id="element-9478">
                          {skin.price}
                        </span>
                        {skin.currency_type === 'Love Tokens' ? <Heart size={12} className="ml-1 text-pink-400" data-id="element-9479" /> : <Diamond size={12} className="ml-1 text-blue-400" data-id="element-9480" />}
                      </div>}
                  </div>
                </div>
                {/* Status indicator */}
                {isCenter && <div className="absolute -top-2 -right-2 z-30" data-id="element-9481">
                    {skin.status === 'Active' && <div className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full" data-id="element-9482">
                        Active
                      </div>}
                    {skin.status === 'Unlocked' && <div className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full" data-id="element-9483">
                        Unlocked
                      </div>}
                    {skin.time_limited && skin.countdown && <div className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full" data-id="element-9484">
                        Limited
                      </div>}
                  </div>}
              </div>;
        })}
        </div>
        {/* Navigation arrows */}
        <button className="absolute left-0 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white z-30" onClick={handlePrev} data-id="element-9485">
          <ChevronLeft size={20} data-id="element-9486" />
        </button>
        <button className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white z-30" onClick={handleNext} data-id="element-9487">
          <ChevronRight size={20} data-id="element-9488" />
        </button>
      </div>
      {/* Pagination dots */}
      <div className="flex justify-center mt-4" data-id="element-9489">
        {MaschaSkinsData.map((_, index) => <button key={index} className={`w-2 h-2 mx-1 rounded-full transition-all ${index === currentIndex ? 'bg-amber-500 w-4' : 'bg-gray-500 hover:bg-gray-400'}`} onClick={() => handleDotClick(index)} data-id="element-9490" />)}
      </div>
    </div>;
};